import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, Building2, CreditCard, Plus, RotateCcw, XCircle } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import TypeToConfirmDialog from '../../../components/TypeToConfirmDialog';
import CreateOrganizationModal from '../../../components/admin/CreateOrganizationModal';
import { tierLabel, useOrgFilterOptions } from '../../../components/admin/useOrgFilterOptions';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import AdminModal from '../../components/AdminModal';
import StatusBadge from '../../components/StatusBadge';
import StatusFilter, { LifecycleStatusFilter } from '../../components/StatusFilter';
import ListPager from '../../components/ListPager';
import AdminListToolbar from '../../components/AdminListToolbar';
import BulkActionsBar, { bulkResultMessage } from '../../components/BulkActionsBar';
import { AdminListResult, useAdminList } from '../../hooks/useAdminList';
import { useRowSelection } from '../../hooks/useRowSelection';
import { formatDate } from '../../lib/format';

const filterSelectClasses =
  'h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';

interface AdminOrgRow {
  id: string;
  name: string;
  org_category: string | null;
  subscription: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  member_count: string;
  workspace_count: string;
}

const parseOrganizations = (raw: unknown): AdminListResult<AdminOrgRow> => {
  const data = (raw as { data?: { organizations?: AdminOrgRow[]; pagination?: { total?: number; hasMore?: boolean } } }).data;
  return {
    rows: data?.organizations ?? [],
    total: data?.pagination?.total ?? 0,
    hasMore: data?.pagination?.hasMore ?? false,
  };
};

interface OrgImpact {
  activeUsers: number;
  activeWorkspaces: number;
  totalWorkspaces: number;
}

const COLUMN_COUNT = 9;

const OrganizationsView: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LifecycleStatusFilter>('active');
  const [category, setCategory] = useState('');
  const [subscription, setSubscription] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<AdminOrgRow | null>(null);
  const [impact, setImpact] = useState<OrgImpact | null>(null);
  const [reactivateTarget, setReactivateTarget] = useState<AdminOrgRow | null>(null);
  const selection = useRowSelection();
  const [bulkAction, setBulkAction] = useState<'deactivate' | 'reactivate' | null>(null);
  const [bulkSubscriptionOpen, setBulkSubscriptionOpen] = useState(false);
  const [bulkSubscription, setBulkSubscription] = useState('');

  const list = useAdminList<AdminOrgRow>({
    queryKey: 'accounts-organizations',
    endpoint: '/admin/accounts/organizations',
    parse: parseOrganizations,
    filters: { status, category: category || undefined, subscription: subscription || undefined },
    initialSort: { key: 'name', direction: 'asc' },
  });

  const filterOptions = useOrgFilterOptions();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-list'] });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.put(`/admin/accounts/organizations/${id}/deactivate`),
    onSuccess: () => {
      toast.success('Organization deactivated');
      setDeactivateTarget(null);
      setImpact(null);
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to deactivate organization');
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => api.put(`/admin/accounts/organizations/${id}/reactivate`),
    onSuccess: () => {
      toast.success('Organization reactivated');
      setReactivateTarget(null);
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to reactivate organization');
    },
  });

  const bulkLifecycleMutation = useMutation({
    mutationFn: (action: 'deactivate' | 'reactivate') =>
      api.post('/admin/accounts/organizations/bulk-lifecycle', { organization_ids: selection.ids, action }),
    onSuccess: (response, action) => {
      toast.success(bulkResultMessage(`${action}d`, response.data.data));
      setBulkAction(null);
      selection.clear();
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Bulk operation failed');
    },
  });

  const bulkSubscriptionMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/accounts/organizations/bulk-subscription', {
        organization_ids: selection.ids,
        subscription: bulkSubscription,
      }),
    onSuccess: (response) => {
      toast.success(bulkResultMessage('updated', response.data.data));
      setBulkSubscriptionOpen(false);
      setBulkSubscription('');
      selection.clear();
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Bulk operation failed');
    },
  });

  const pageIds = list.rows.map((org) => org.id);

  const openDeactivate = async (org: AdminOrgRow) => {
    setImpact(null);
    setDeactivateTarget(org);
    try {
      const response = await api.get(`/admin/accounts/organizations/${org.id}/impact`);
      setImpact(response.data.data as OrgImpact);
    } catch {
      // Dialog still works without the impact preview
    }
  };

  return (
    <div className="space-y-4">
      <AdminListToolbar
        search={list.search}
        onSearch={list.setSearch}
        searchPlaceholder="Search organizations…"
        searchAriaLabel="Search organizations"
        actions={
          <Button variant="primary" size="sm" icon={Plus} onClick={() => setCreateOpen(true)}>
            Create Organization
          </Button>
        }
      >
        <StatusFilter value={status} onChange={setStatus} />
        <select
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          aria-label="Filter by category"
          className={filterSelectClasses}
        >
          <option value="">All categories</option>
          {(filterOptions.data?.categories ?? []).map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <select
          value={subscription}
          onChange={(event) => setSubscription(event.target.value)}
          aria-label="Filter by subscription"
          className={filterSelectClasses}
        >
          <option value="">All subscriptions</option>
          {(filterOptions.data?.subscriptionTiers ?? []).map((tier) => (
            <option key={tier.key} value={tier.key}>
              {tier.label}
            </option>
          ))}
        </select>
      </AdminListToolbar>

      <BulkActionsBar count={selection.count} noun="organization" onClear={selection.clear}>
        <Button variant="outline" size="sm" icon={XCircle} onClick={() => setBulkAction('deactivate')}>
          Deactivate
        </Button>
        <Button variant="outline" size="sm" icon={RotateCcw} onClick={() => setBulkAction('reactivate')}>
          Reactivate
        </Button>
        <Button variant="outline" size="sm" icon={CreditCard} onClick={() => setBulkSubscriptionOpen(true)}>
          Change subscription…
        </Button>
      </BulkActionsBar>

      {list.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load organizations. Please try again.
          </p>
          <Button variant="outline" size="sm" onClick={() => list.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <StandardTable horizontalScroll loading={list.isFetching && !list.isLoading}>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell isCheckbox>
                <input
                  type="checkbox"
                  aria-label="Select all organizations on this page"
                  checked={selection.allSelected(pageIds)}
                  onChange={(event) => selection.setPage(pageIds, event.target.checked)}
                />
              </StandardTableHeaderCell>
              <StandardTableHeaderCell sortable sortKey="name" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Name
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md" sortable sortKey="org_category" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Category
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg" sortable sortKey="subscription" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Subscription
              </StandardTableHeaderCell>
              <StandardTableHeaderCell align="right" columnType="fit" sortable sortKey="member_count" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Members
              </StandardTableHeaderCell>
              <StandardTableHeaderCell align="right" columnType="fit" sortable sortKey="workspace_count" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Workspaces
              </StandardTableHeaderCell>
              <StandardTableHeaderCell sortable sortKey="is_active" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Status
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg" columnType="fit" sortable sortKey="created_at" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Created
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="actions">Actions</StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {list.isLoading ? (
              <LoadingTableState colSpan={COLUMN_COUNT} />
            ) : list.rows.length === 0 ? (
              <EmptyTableState
                colSpan={COLUMN_COUNT}
                message={list.search ? `No organizations match "${list.search}"` : 'No organizations found'}
                icon={<Building2 className="h-8 w-8" />}
              />
            ) : (
              list.rows.map((org) => (
                <StandardTableRow
                  key={org.id}
                  selected={selection.has(org.id)}
                  onClick={() => navigate(`/admin-next/accounts/organizations/${org.id}`)}
                >
                  <StandardTableCell isCheckbox>
                    <div onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${org.name}`}
                        checked={selection.has(org.id)}
                        onChange={() => selection.toggle(org.id)}
                      />
                    </div>
                  </StandardTableCell>
                  <StandardTableCell>
                    <span className="font-medium text-text-primary dark:text-text-dark-primary">
                      {org.name}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">{org.org_category ?? '—'}</StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{org.subscription ? tierLabel(filterOptions.data, org.subscription) : '—'}</StandardTableCell>
                  <StandardTableCell align="right">{org.member_count}</StandardTableCell>
                  <StandardTableCell align="right">{org.workspace_count}</StandardTableCell>
                  <StandardTableCell>
                    <StatusBadge active={org.is_active} />
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{formatDate(org.created_at)}</StandardTableCell>
                  <StandardTableCell>
                    <div onClick={(event) => event.stopPropagation()}>
                      {org.is_active ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={XCircle}
                          onClick={() => openDeactivate(org)}
                          ariaLabel={`Deactivate ${org.name}`}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={RotateCcw}
                          onClick={() => setReactivateTarget(org)}
                          ariaLabel={`Reactivate ${org.name}`}
                        >
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
      )}

      {!list.isError && (
        <ListPager
          offset={list.offset}
          limit={list.limit}
          total={list.total}
          page={list.page}
          pageCount={list.pageCount}
          hasPrev={list.hasPrev}
          hasNext={list.hasNext}
          onPrev={list.prevPage}
          onNext={list.nextPage}
          noun="organizations"
        />
      )}

      <CreateOrganizationModal
        isOpen={createOpen}
        onClose={() => {
          setCreateOpen(false);
          invalidate();
        }}
      />

      <TypeToConfirmDialog
        isOpen={deactivateTarget !== null}
        title="Deactivate organization"
        message={
          deactivateTarget
            ? `Deactivating "${deactivateTarget.name}" will suspend access for ${
                impact ? `${impact.activeUsers} active user${impact.activeUsers === 1 ? '' : 's'}` : 'its members'
              } and make ${
                impact ? `${impact.activeWorkspaces} of its ${impact.totalWorkspaces} workspace${impact.totalWorkspaces === 1 ? '' : 's'}` : 'its workspaces'
              } unavailable. Nothing is deleted; the organization can be reactivated later. Type the organization name to confirm.`
            : ''
        }
        confirmationWord={deactivateTarget?.name ?? ''}
        confirmText="Deactivate"
        onConfirm={() => deactivateTarget && deactivateMutation.mutate(deactivateTarget.id)}
        onCancel={() => {
          setDeactivateTarget(null);
          setImpact(null);
        }}
        isLoading={deactivateMutation.isLoading}
      />

      <ConfirmationDialog
        isOpen={reactivateTarget !== null}
        title="Reactivate organization"
        message={
          reactivateTarget
            ? `Reactivate "${reactivateTarget.name}"? Members regain access and its workspaces become available again, exactly as they were before deactivation.`
            : ''
        }
        confirmText="Reactivate"
        variant="info"
        onConfirm={() => reactivateTarget && reactivateMutation.mutate(reactivateTarget.id)}
        onCancel={() => setReactivateTarget(null)}
      />

      <TypeToConfirmDialog
        isOpen={bulkAction === 'deactivate'}
        title="Deactivate selected organizations"
        message={`Deactivating ${selection.count} organization${selection.count === 1 ? '' : 's'} suspends access for their members and makes their workspaces unavailable. Nothing is deleted; they can be reactivated later. Already-deactivated organizations are skipped. Type DEACTIVATE to confirm.`}
        confirmationWord="DEACTIVATE"
        confirmText="Deactivate All"
        onConfirm={() => bulkLifecycleMutation.mutate('deactivate')}
        onCancel={() => setBulkAction(null)}
        isLoading={bulkLifecycleMutation.isLoading}
      />

      <ConfirmationDialog
        isOpen={bulkAction === 'reactivate'}
        title="Reactivate selected organizations"
        message={`Reactivate ${selection.count} organization${selection.count === 1 ? '' : 's'}? Members regain access and their workspaces become available again. Already-active organizations are skipped.`}
        confirmText="Reactivate All"
        variant="info"
        onConfirm={() => bulkLifecycleMutation.mutate('reactivate')}
        onCancel={() => setBulkAction(null)}
      />

      <AdminModal
        isOpen={bulkSubscriptionOpen}
        title={`Change subscription for ${selection.count} organization${selection.count === 1 ? '' : 's'}`}
        onClose={() => setBulkSubscriptionOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setBulkSubscriptionOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={bulkSubscription === ''}
              loading={bulkSubscriptionMutation.isLoading}
              onClick={() => bulkSubscriptionMutation.mutate()}
            >
              Apply
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label htmlFor="bulk-subscription" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
              New subscription tier *
            </label>
            <select
              id="bulk-subscription"
              value={bulkSubscription}
              onChange={(event) => setBulkSubscription(event.target.value)}
              className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
            >
              <option value="">Select a tier…</option>
              {(filterOptions.data?.subscriptionTiers ?? []).map((tier) => (
                <option key={tier.key} value={tier.key}>
                  {tier.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            Each change is audit-logged with the previous value. Organizations already on the selected tier are skipped.
          </p>
        </div>
      </AdminModal>
    </div>
  );
};

export default OrganizationsView;
